import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  startOfMonth, endOfMonth, subMonths, subDays, format
} from 'date-fns';

const PRESETS = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_7', label: 'Last 7 Days' },
  { key: 'last_30', label: 'Last 30 Days' },
  { key: 'custom', label: 'Custom Range' },
];

function getPresetDates(key) {
  const now = new Date();
  switch (key) {
    case 'this_month':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'last_month': {
      const prev = subMonths(now, 1);
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }
    case 'last_7':
      return { start: subDays(now, 6), end: now };
    case 'last_30':
      return { start: subDays(now, 29), end: now };
    default:
      return { start: startOfMonth(now), end: endOfMonth(now) };
  }
}

export const PeriodSelector = ({ startDate, endDate, onChange, preset: controlledPreset, onPresetChange }) => {
  const activeLabel = useMemo(() => {
    if (controlledPreset === 'custom') {
      return `${format(startDate, 'dd MMM')} – ${format(endDate, 'dd MMM yyyy')}`;
    }
    return PRESETS.find(p => p.key === controlledPreset)?.label || 'This Month';
  }, [controlledPreset, startDate, endDate]);

  const handlePreset = (key) => {
    onPresetChange(key);
    if (key !== 'custom') {
      const { start, end } = getPresetDates(key);
      onChange(start, end);
    }
  };

  const handleCustomStart = (date) => {
    if (date) {
      onPresetChange('custom');
      onChange(date, endDate);
    }
  };

  const handleCustomEnd = (date) => {
    if (date) {
      onPresetChange('custom');
      onChange(startDate, date);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="period-selector">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9" data-testid="period-preset-trigger">
            <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
            <span className="font-medium">{activeLabel}</span>
            <ChevronDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {PRESETS.map(p => (
            <DropdownMenuItem
              key={p.key}
              onClick={() => handlePreset(p.key)}
              className={controlledPreset === p.key ? 'bg-accent font-medium' : ''}
              data-testid={`period-preset-${p.key}`}
            >
              {p.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {controlledPreset === 'custom' && (
        <>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 w-[130px] justify-start text-left font-normal" data-testid="period-start-date">
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                {format(startDate, 'dd MMM yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={startDate} onSelect={handleCustomStart} initialFocus />
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">to</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 w-[130px] justify-start text-left font-normal" data-testid="period-end-date">
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                {format(endDate, 'dd MMM yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={endDate} onSelect={handleCustomEnd} initialFocus />
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
};
